/**
 * Condition objects service — client surface for typed conditions (C1).
 *
 * PRD: docs/9-exploration/CONDITION-MONITORING-AND-DECISION-EXPIRY-PRD.md §4.
 * Schema + RPCs: supabase/migrations/699_condition_objects.sql.
 * Judgment (state machine mirror, breach, staleness, rollup):
 * src/lib/conditionObjects.ts → supabase/functions/_shared/conditionHealthCore.ts.
 *
 * All writes go through the SECURITY DEFINER RPCs (Build Contract §2.3a) — this
 * module never inserts or updates a condition row directly. Reads go through
 * the `condition_health` view. The RPCs raise on refusal; refusals surface here
 * as { ok: false, error } carrying the database's own sentence, which is
 * already written in user language.
 *
 * ── DB CONTRACT NOTE ────────────────────────────────────────────────────────
 * The repo rule is that new data-access code uses `getTypedSupabaseClient()` so
 * schema drift is a compile error. That is not yet possible here: migration 699
 * is written but NOT applied to any environment, so `condition_objects`,
 * `condition_health` and the three RPCs are absent from
 * `src/types/database.generated.ts`, and typing against it would not compile.
 * Regenerating types requires an applied migration, which is the integrator's
 * serialized step, not this session's.
 *
 * So this module uses the base client and declares its own row shape below,
 * which the view is the authority for. THE FOLLOW-UP IS EXACT: once 699 is
 * applied, run `npm run db:types`, then switch `client()` to
 * `getTypedSupabaseClient()` and replace `ConditionHealthRow` with
 * `Database['public']['Views']['condition_health']['Row']`. Until then, a
 * column rename in the view is a runtime error here rather than a compile
 * error — the reason the contract exists, and the reason this note is loud.
 */

import { getSupabaseClient } from './supabaseClient';
import { getTypedSupabaseClient } from './typedSupabaseClient';
import type { ConditionKind, ConditionStatus, ThresholdComparator } from './conditionObjects';

/**
 * A row of `condition_health` (migration 699). Mirrors the view exactly; see
 * the DB contract note above for when this is replaced by generated types.
 */
export interface ConditionHealthRow {
  id: string;
  workspace_id: string;
  subject_kind: 'decision' | 'commitment';
  decision_id: string | null;
  plan_cycle_id: string | null;
  item_id: string | null;
  subject_title: string | null;
  plan_cycle_name: string | null;
  plan_cycle_status: string | null;
  depends_on_item_title: string | null;
  kind: ConditionKind;
  statement: string;
  binding: unknown;
  depends_on_item_id: string | null;
  attested_by: string | null;
  term_id: string | null;
  term_condition_text: string | null;
  term_status: string | null;
  status: ConditionStatus;
  status_changed_at: string | null;
  status_changed_by: string | null;
  status_basis: string | null;
  last_evaluated_at: string | null;
  evaluation_basis: string | null;
  last_observed_value: number | string | null;
  last_observed_at: string | null;
  last_observed_by: string | null;
  last_observed_basis: string | null;
  days_since_evaluated: number | null;
  source_free_text: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ConditionWriteResult {
  ok: boolean;
  conditionId?: string;
  error?: string;
}

const VIEW = 'condition_health';

function client() {
  return getSupabaseClient();
}

function toResult(data: unknown, error: { message: string } | null): ConditionWriteResult {
  if (error) return { ok: false, error: error.message };
  return { ok: true, conditionId: typeof data === 'string' ? data : undefined };
}

// ── Reads ────────────────────────────────────────────────────────────────────

export async function listConditionsForDecision(
  decisionId: string,
): Promise<{ rows: ConditionHealthRow[]; error?: string }> {
  const { data, error } = await client()
    .from(VIEW)
    .select('*')
    .eq('decision_id', decisionId)
    .order('created_at', { ascending: true });
  if (error) return { rows: [], error: error.message };
  return { rows: (data ?? []) as ConditionHealthRow[] };
}

/** Every condition on one commitment, keyed by the durable identity. */
export async function listConditionsForCommitment(
  planCycleId: string,
  itemId: string,
): Promise<{ rows: ConditionHealthRow[]; error?: string }> {
  const { data, error } = await client()
    .from(VIEW)
    .select('*')
    .eq('plan_cycle_id', planCycleId)
    .eq('item_id', itemId)
    .order('created_at', { ascending: true });
  if (error) return { rows: [], error: error.message };
  return { rows: (data ?? []) as ConditionHealthRow[] };
}

/** Every condition attached to any commitment on a cycle — the plan readout. */
export async function listConditionsForCycle(
  planCycleId: string,
): Promise<{ rows: ConditionHealthRow[]; error?: string }> {
  const { data, error } = await client()
    .from(VIEW)
    .select('*')
    .eq('plan_cycle_id', planCycleId)
    .order('created_at', { ascending: true });
  if (error) return { rows: [], error: error.message };
  return { rows: (data ?? []) as ConditionHealthRow[] };
}

/**
 * The `{kind:'commitment'}` ConditionsPanel subject is keyed on the durable
 * planning identity `(plan_cycle_id, item_id)` — a plan commitment (Doc 103) —
 * which is NOT the same object as a Commitment Registry entry (`commitments`
 * table, customer/partner/regulatory promises). A registry commitment has
 * no plan_cycle_id of its own; it only has linked items. For a linked item to
 * carry conditions, that item must independently be part of an active plan
 * cycle (a row in `initiatives`, on some plan version of that cycle, not
 * dropped).
 *
 * This resolves that eligibility for a set of items: for each item, the
 * active plan cycle(s) it currently participates in, if any. Three flat
 * queries rather than a PostgREST embed so the typed client can check every
 * column at compile time.
 *
 * Approximation, stated plainly: this checks ANY non-dropped initiative row
 * for the item on ANY version of an active plan cycle — not only the cycle's
 * latest version. For the purpose this exists (deciding whether the
 * "Conditions" action is offerable at all), that is the right amount of
 * precision; a stricter "latest version only" check belongs to whichever
 * surface renders the plan itself.
 */
export interface PlanCommitmentEligibility {
  itemId: string;
  planCycleId: string;
  planCycleName: string;
}

export async function findActivePlanCommitmentsForItems(
  itemIds: string[]
): Promise<{ rows: PlanCommitmentEligibility[]; error?: string }> {
  if (itemIds.length === 0) return { rows: [] };
  const db = getTypedSupabaseClient();

  const { data: inits, error: initErr } = await db
    .from('initiatives')
    .select('item_id, plan_version_id')
    .in('item_id', itemIds)
    .neq('status', 'dropped')
    .not('item_id', 'is', null);
  if (initErr) return { rows: [], error: initErr.message };
  const initRows = inits ?? [];
  const versionIds = [...new Set(initRows.map((i) => i.plan_version_id))];
  if (versionIds.length === 0) return { rows: [] };

  const { data: versions, error: verErr } = await db
    .from('plan_versions')
    .select('id, plan_cycle_id')
    .in('id', versionIds);
  if (verErr) return { rows: [], error: verErr.message };
  const cycleByVersion = new Map((versions ?? []).map((v) => [v.id, v.plan_cycle_id]));
  const cycleIds = [...new Set((versions ?? []).map((v) => v.plan_cycle_id))];
  if (cycleIds.length === 0) return { rows: [] };

  const { data: cycles, error: cycErr } = await db
    .from('plan_cycles')
    .select('id, name, status')
    .in('id', cycleIds)
    .eq('status', 'active');
  if (cycErr) return { rows: [], error: cycErr.message };
  const activeCycleNames = new Map((cycles ?? []).map((c) => [c.id, c.name]));

  const seen = new Set<string>();
  const rows: PlanCommitmentEligibility[] = [];
  for (const init of initRows) {
    if (!init.item_id) continue;
    const cycleId = cycleByVersion.get(init.plan_version_id);
    if (!cycleId) continue;
    const planCycleName = activeCycleNames.get(cycleId);
    if (planCycleName === undefined) continue; // cycle exists but isn't active
    const key = `${init.item_id}:${cycleId}`;
    if (seen.has(key)) continue;
    seen.add(key);
    rows.push({ itemId: init.item_id, planCycleId: cycleId, planCycleName });
  }
  return { rows };
}

// ── Writes (RPC only) ────────────────────────────────────────────────────────

export interface ThresholdBindingInput {
  comparator: ThresholdComparator;
  value: number;
  unit?: string;
  /** Where the number comes from, in the reader's words. */
  source_label?: string;
}

export interface AttachConditionInput {
  kind: ConditionKind;
  statement: string;
  /** Required for kind='threshold'. */
  threshold?: ThresholdBindingInput;
  /** Required for kind='external_signal'. */
  signalRef?: Record<string, unknown>;
  /** Required for kind='dependency'. */
  dependsOnItemId?: string;
  /** Optional: the stopping rule (mig 680) this condition argues for. */
  termId?: string;
  /** The mig-464 free text this row was split from, when applicable. */
  sourceFreeText?: string;
}

export interface AttachDecisionConditionInput extends AttachConditionInput {
  decisionId: string;
}

export interface AttachCommitmentConditionInput extends AttachConditionInput {
  planCycleId: string;
  itemId: string;
}

function bindingFor(input: AttachConditionInput): Record<string, unknown> | undefined {
  if (input.kind === 'threshold') return input.threshold as unknown as Record<string, unknown>;
  if (input.kind === 'external_signal') return input.signalRef;
  return undefined;
}

export async function attachDecisionCondition(
  input: AttachDecisionConditionInput,
): Promise<ConditionWriteResult> {
  const { data, error } = await client().rpc('attach_condition', {
    p_subject_kind: 'decision',
    p_decision_id: input.decisionId,
    p_plan_cycle_id: null,
    p_item_id: null,
    p_kind: input.kind,
    p_statement: input.statement,
    p_binding: bindingFor(input) ?? null,
    p_depends_on_item_id: input.dependsOnItemId ?? null,
    p_term_id: input.termId ?? null,
    p_source_free_text: input.sourceFreeText ?? null,
  });
  return toResult(data, error);
}

export async function attachCommitmentCondition(
  input: AttachCommitmentConditionInput,
): Promise<ConditionWriteResult> {
  const { data, error } = await client().rpc('attach_condition', {
    p_subject_kind: 'commitment',
    p_decision_id: null,
    p_plan_cycle_id: input.planCycleId,
    p_item_id: input.itemId,
    p_kind: input.kind,
    p_statement: input.statement,
    p_binding: bindingFor(input) ?? null,
    p_depends_on_item_id: input.dependsOnItemId ?? null,
    p_term_id: input.termId ?? null,
    p_source_free_text: input.sourceFreeText ?? null,
  });
  return toResult(data, error);
}

/**
 * Report a reading against a condition. Never changes its status — the watcher
 * proposes on the reading, and moving the condition is a separate human act.
 */
export async function recordConditionObservation(
  conditionId: string,
  value: number | null,
  basis: string,
  observedAt?: string,
): Promise<ConditionWriteResult> {
  const { data, error } = await client().rpc('record_condition_observation', {
    p_condition_id: conditionId,
    p_value: value,
    p_basis: basis,
    ...(observedAt ? { p_observed_at: observedAt } : {}),
  });
  return toResult(data, error);
}

export async function setConditionStatus(
  conditionId: string,
  status: ConditionStatus,
  basis: string,
): Promise<ConditionWriteResult> {
  const { data, error } = await client().rpc('set_condition_status', {
    p_condition_id: conditionId,
    p_status: status,
    p_basis: basis,
  });
  return toResult(data, error);
}
